// © Microsoft Corporation. All rights reserved.

import { PerfTimeline } from "@msnews/diagnostics";
import React from "react";

/**
 * Returns an HOC for tracking perf timeline of a React.Component
 * @param WrappedComponent - the component to instrument perf for
 * @param perfTimeline - the {PerfTimeline} object to use for tracking perf.
 *      This is normally created in the parent context when instantating the component
 *      and passed down to the HOC.
 */
export function instrumentPerf<TProps, TState>(
    WrappedComponent: React.ComponentType<TProps>,
    perfTimeline: PerfTimeline
): React.ComponentClass<TProps> {

    return class ComponentPerfTimeline extends React.Component<TProps, TState, never> {

        /**
         * The last render action's endMeasure method to be used to mark the end of the render
         * cycle when mount or update happens next
         */
        private lastRenderEndMeasure: () => void;

        /** React render lifecycle handler  */
        public render(): JSX.Element {
            this.lastRenderEndMeasure = perfTimeline.startMeasure("render").endMeasure;
            return <WrappedComponent {...this.props} />;
        }

        /** React componentDidMount lifecycle handler  */
        public componentDidMount(): void {
            this.lastRenderEndMeasure();
        }

        /** React componentDidUpdate lifecycle handler  */
        public componentDidUpdate(): void {
            this.lastRenderEndMeasure();
        }
    };
}